import React, { useCallback, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from '@mui/material';

import BundlePropertyPicker, {
  PropertyOption,
} from '~/components/Accommodation/Pages/Bundles/components/BundlePropertyPicker';
import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';

import { useDeleteCampaign, useUpdateCampaign } from '~/hooks/apis/accommodation/useCampaigns';
import useAppNavigation from '~/hooks/useAppNavigation';

import { Campaign, UpdateCampaignPayload } from '~/services/AccommodationService';

import { parseStrictInteger } from '~/utils/valueParsers';

import { ALL_CAMPAIGN_STATUSES } from '../constants';

import CampaignOccupancyEditor, { CampaignDefaultOccupancy } from './CampaignOccupancyEditor';
import CampaignScheduleEditor, { CampaignScheduleRow, sanitizeSchedulesForSubmit } from './CampaignScheduleEditor';

const DEFAULT_OCCUPANCY: CampaignDefaultOccupancy = { adults: 2 };

interface FormState {
  name: string;
  displayName: string;
  subtitle: string;
  status: NonNullable<UpdateCampaignPayload['status']>;
  properties: Array<PropertyOption>;
  schedules: Array<CampaignScheduleRow>;
  marketedLos: string;
  defaultOccupancy: CampaignDefaultOccupancy | null;
}

const stateFromCampaign = (campaign: Campaign): FormState => ({
  name: campaign.name,
  displayName: campaign.displayName ?? '',
  subtitle: campaign.subtitle ?? '',
  status: campaign.status,
  properties: campaign.propertyIds.map((id) => ({ id, name: id })),
  schedules: campaign.schedules.map((s) => ({
    brands: s.brands ?? [],
    activeStartDate: s.activeStartDate ?? '',
    activeEndDate: s.activeEndDate ?? '',
    travelStartDate: s.travelStartDate ?? '',
    travelEndDate: s.travelEndDate ?? '',
  })),
  marketedLos: campaign.marketedLos != null ? String(campaign.marketedLos) : '',
  defaultOccupancy: campaign.defaultOccupancy,
});

interface Props {
  campaign: Campaign;
}

function CampaignForm({ campaign }: Props) {
  const { push } = useAppNavigation();
  const showConfirmDialog = useConfirmDialog();
  const [state, setState] = useState<FormState>(() => stateFromCampaign(campaign));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const updateCampaignMutation = useUpdateCampaign();
  const deleteCampaignMutation = useDeleteCampaign();

  const handleChange = useCallback(<K extends keyof FormState>(key: K, value: FormState[K]) => {
    setState((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleSave = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault();
      setErrorMessage(null);
      try {
        await updateCampaignMutation.mutateAsync({
          campaignId: campaign.id,
          payload: {
            name: state.name,
            displayName: state.displayName.trim() || null,
            subtitle: state.subtitle.trim() || null,
            status: state.status,
            propertyIds: state.properties.map((p) => p.id),
            schedules: sanitizeSchedulesForSubmit(state.schedules),
            marketedLos: parseStrictInteger(state.marketedLos, 1) ?? null,
            defaultOccupancy: state.defaultOccupancy,
          },
        });
      } catch (err: unknown) {
        setErrorMessage(err instanceof Error ? err.message : 'Failed to save campaign');
      }
    },
    [state, updateCampaignMutation, campaign.id],
  );

  const handleDelete = useCallback(async () => {
    const confirmed = await showConfirmDialog({
      title: 'Delete campaign',
      description: `Soft-delete campaign "${campaign.name}"? Recovery is possible by Engineering only.`,
    });
    if (!confirmed) {
      return;
    }
    try {
      await deleteCampaignMutation.mutateAsync({ campaignId: campaign.id });
      push('/accommodation/campaigns');
    } catch (err: unknown) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to delete campaign');
    }
  }, [deleteCampaignMutation, campaign.id, campaign.name, push, showConfirmDialog]);

  const isSubmitting = updateCampaignMutation.isPending;
  const isDeleting = deleteCampaignMutation.isPending;
  const canSubmit = !!state.name.trim() && state.properties.length > 0;

  return (
    <Box>
      <Box component="form" onSubmit={handleSave}>
        <Stack spacing={2}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

          <TextField
            label="Campaign Name"
            helperText="Internal identifier — not shown to customers"
            value={state.name}
            onChange={(e) => handleChange('name', e.target.value)}
            required
            fullWidth
          />

          <TextField
            label="Display Name"
            helperText="Customer-facing name. Leave blank to fall back to the campaign name"
            value={state.displayName}
            onChange={(e) => handleChange('displayName', e.target.value)}
            fullWidth
          />

          <TextField
            label="Subtitle"
            helperText="Subject line / preheader copy"
            value={state.subtitle}
            onChange={(e) => handleChange('subtitle', e.target.value)}
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={state.status}
              onChange={(e) => handleChange('status', e.target.value as FormState['status'])}
            >
              {ALL_CAMPAIGN_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <CampaignScheduleEditor value={state.schedules} onChange={(next) => handleChange('schedules', next)} />

          <TextField
            label="Marketed Length of Stay (nights)"
            helperText="Informational — not an eligibility restriction. Leave blank to fall back to 1 night"
            type="number"
            inputProps={{ min: 1 }}
            value={state.marketedLos}
            onChange={(e) => handleChange('marketedLos', e.target.value)}
            sx={{ width: 280 }}
          />

          <Box border={1} borderColor="divider" borderRadius={1} p={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={state.defaultOccupancy !== null}
                  onChange={(e) => handleChange('defaultOccupancy', e.target.checked ? DEFAULT_OCCUPANCY : null)}
                />
              }
              label="Set a custom default occupancy"
            />
            {state.defaultOccupancy !== null && (
              <Box mt={1}>
                <CampaignOccupancyEditor
                  value={state.defaultOccupancy}
                  onChange={(next) => handleChange('defaultOccupancy', next)}
                />
              </Box>
            )}
          </Box>

          <BundlePropertyPicker
            value={state.properties}
            onChange={(next) => handleChange('properties', next)}
            initialIds={campaign.propertyIds}
          />

          <Box display="flex" justifyContent="flex-end">
            <Button type="submit" variant="contained" disabled={!canSubmit || isSubmitting}>
              {isSubmitting ? 'Saving…' : 'Save Campaign'}
            </Button>
          </Box>
        </Stack>
      </Box>

      <Box mt={6} pt={3} borderTop={1} borderColor="divider">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Box fontWeight={500}>Danger zone</Box>
            <Box color="text.secondary" fontSize={14}>
              Soft-delete this campaign. Recovery is possible by Engineering only.
            </Box>
          </Box>
          <Button color="error" variant="outlined" onClick={handleDelete} disabled={isDeleting}>
            {isDeleting ? 'Deleting…' : 'Delete Campaign'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}

export default CampaignForm;
